import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { useIntl } from "react-intl";

import EmailTone from "@mapmycustomers/shared/enum/email/EmailTone";
import GenerateEmailResponse from "@mapmycustomers/shared/types/email/GenerateEmailResponse";
import { EntitiesSupportedByEmailFeature, EntityType } from "@mapmycustomers/shared/types/entity";
import { Divider } from "@mapmycustomers/ui";

import { generateEmailContent, submitEmailAiFeedback } from "@app/store/email/actions";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import isEmailBodyEmpty from "@app/util/email/isEmailBodyEmpty";

import styles from "./AIInlineWriter.module.scss";
import GeneratedContent from "./GeneratedContent";
import useTypewriterAnimation from "./GeneratedContent/useTypewriterAnimation";
import PromptInput from "./PromptInput";

interface AIInlineWriterProps {
  emailBody?: string;
  emailSubject?: string;
  entityId?: EntitiesSupportedByEmailFeature["id"];
  entityType?: EntityType;
  onClose: () => void;
  onInsert: (body: string, subject?: string) => void;
  signatureText: string;
}

const AIInlineWriter = ({
  emailBody,
  emailSubject,
  entityId,
  entityType,
  onClose,
  onGenerateEmailContent,
  onInsert,
  onSubmitEmailAiFeedback,
  signatureText,
}: AIInlineWriterProps & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("AI Writer", useAnalytics());

  const [prompt, setPrompt] = useState("");
  const [inputFocused, setInputFocused] = useState(true);
  const [generating, setGenerating] = useState(false);

  const [generatedContent, setGeneratedContent] = useState<GenerateEmailResponse>();
  const [previousGeneratedContent, setPreviousGeneratedContent] = useState<
    GenerateEmailResponse | undefined
  >();
  const [lastPrompt, setLastPrompt] = useState("");
  const [selectedTone, setSelectedTone] = useState<EmailTone | undefined>();
  const abortControllerRef = useRef<AbortController>();

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const hasContent = !!generatedContent?.body || !isEmailBodyEmpty(emailBody ?? "", signatureText);

  const existingEmailContext = useMemo(() => {
    const body = generatedContent?.body ?? emailBody ?? "";
    if (isEmailBodyEmpty(body, signatureText)) {
      return undefined;
    }
    return { body, subject: emailSubject ?? "" };
  }, [emailBody, emailSubject, generatedContent?.body, signatureText]);

  const handleGenerate = useCallback(
    (promptText: string, regenerate?: boolean) => {
      if (!promptText.trim()) {
        return;
      }
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const previousContent = generatedContent;
      setLastPrompt(promptText);
      setGenerating(true);
      const trackingProps = {
        regenerate: !!regenerate,
        ...(selectedTone ? { tone: selectedTone } : {}),
      };
      onGenerateEmailContent({
        callback: (data) => {
          setGenerating(false);
          setPreviousGeneratedContent(previousContent);
          setGeneratedContent(data);
          setPrompt("");
          analytics.completed([], trackingProps);
        },
        email: existingEmailContext,
        entityId,
        entityType,
        failureCallback: () => {
          setGenerating(false);
          analytics.failed([], trackingProps);
        },
        prompt: promptText,
        regenerate,
        signal: abortControllerRef.current.signal,
        tone: selectedTone,
      });
    },
    [
      analytics,
      entityId,
      entityType,
      existingEmailContext,
      generatedContent,
      onGenerateEmailContent,
      selectedTone,
    ]
  );

  const handlePromptSubmit = useCallback(() => {
    analytics.clicked(["Submit"], {
      describing: !!generatedContent?.body,
      ...(selectedTone ? { tone: selectedTone } : {}),
    });
    handleGenerate(prompt);
  }, [analytics, generatedContent?.body, handleGenerate, prompt, selectedTone]);

  const handleRegenerate = useCallback(() => {
    analytics.clicked(["Regenerate"]);
    handleGenerate(lastPrompt, true);
  }, [analytics, handleGenerate, lastPrompt]);

  const handleStop = useCallback(() => {
    analytics.clicked(["Stop"]);
    abortControllerRef.current?.abort();
    setGenerating(false);
  }, [analytics]);

  const displayContent = useMemo(() => {
    if (!generatedContent?.body) {
      return emailBody ?? "";
    }
    return generatedContent.body;
  }, [emailBody, generatedContent?.body]);

  const { animating, skipNextAnimation, visibleHtml } = useTypewriterAnimation(
    displayContent,
    generating,
    !!generatedContent?.body
  );

  const handleUndo = useCallback(() => {
    analytics.clicked(["Undo"]);
    skipNextAnimation();
    setGeneratedContent(previousGeneratedContent);
    setLastPrompt("");
    setPreviousGeneratedContent(undefined);
  }, [analytics, previousGeneratedContent, skipNextAnimation]);

  const handleReport = useCallback(
    (comment?: string) => {
      analytics.completed(["Report"], { hasComment: !!comment?.trim() });
      if (generatedContent?.requestId) {
        onSubmitEmailAiFeedback({ comment, requestId: generatedContent.requestId });
      }
    },
    [analytics, generatedContent?.requestId, onSubmitEmailAiFeedback]
  );

  const handleReportOpen = useCallback(() => {
    analytics.initiated(["Report"]);
  }, [analytics]);

  const handleInsert = useCallback(() => {
    analytics.clicked(["Accept"]);
    if (generatedContent) {
      onInsert(generatedContent.body, generatedContent.subject);
    }
    onClose();
  }, [analytics, generatedContent, onClose, onInsert]);
  const showGeneratedContent = hasContent || generating || animating;
  const showActionButtons = !generating && !animating;

  return (
    <div
      className={cn(styles.containerWrapper, {
        [styles.focused]: inputFocused,
        [styles.hasContent]: hasContent && !generating,
      })}
    >
      <div className={styles.container}>
        {showGeneratedContent && (
          <div className={styles.scrollableContent}>
            <GeneratedContent
              canRegenerate={!!lastPrompt.trim().length}
              content={visibleHtml}
              generating={generating}
              onRegenerate={handleRegenerate}
              onReport={handleReport}
              onReportOpen={handleReportOpen}
              onUndo={previousGeneratedContent?.body ? handleUndo : undefined}
              showActionButtons={showActionButtons}
            />
          </div>
        )}
        {showActionButtons && showGeneratedContent && <Divider className={styles.divider} />}
        {!animating && (
          <PromptInput
            focused={inputFocused}
            generating={generating}
            hasGeneratedContent={!!generatedContent?.body}
            onChange={setPrompt}
            onClose={onClose}
            onConfirm={handleInsert}
            onFocusChange={setInputFocused}
            onStop={handleStop}
            onSubmit={handlePromptSubmit}
            onToneSelect={setSelectedTone}
            placeholder={
              hasContent
                ? intl.formatMessage({
                    id: "email.creation.aiInlineWriter.edit.placeholder",
                    defaultMessage: "Describe how to edit the text",
                    description: "Edit email prompt placeholder",
                  })
                : undefined
            }
            selectedTone={selectedTone}
            value={generating ? lastPrompt : prompt}
          />
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  onGenerateEmailContent: generateEmailContent.request,
  onSubmitEmailAiFeedback: submitEmailAiFeedback,
};

export default connect(null, mapDispatchToProps)(AIInlineWriter);
